import { Observable, defer, of } from 'rxjs';
import { finalize, shareReplay, tap } from 'rxjs/operators';
import type { IGetChallengesResponse } from 'services/casino';

/**
 * Session cache for player-scoped GETs that must not re-fire on Explore
 * timeframe/insight reloads (favorites, playlist, selected EGMs, messages,
 * linked accounts). Keyed by appUserPk (+ casinoId when relevant).
 *
 * - First call for a key hits the network; concurrent callers share one
 *   in-flight Observable.
 * - After success, later callers get the cached payload (Redux callers may
 *   re-Set; selected-EGMs callers re-run onLoaded without a network hit).
 * - Errors do not cache, so the next call can retry.
 * - Invalidate on casino re-entry / logout / tests. Add/remove updates Redux
 *   optimistically — no refetch required.
 * - Optional `ttlMs` (per call) treats a cached payload older than the TTL as
 *   missing, for lists that can change server-side (e.g. challenges).
 * - A request that was invalidated while in flight never writes its payload
 *   back and never removes a newer in-flight entry for the same key.
 */

export type PlayerScopedBucket<T> = {
  data: Map<string, T>;
  inflight: Map<string, Observable<T>>;
  /** When each cached payload was stored (ms). Only consulted when a TTL is passed. */
  loadedAt: Map<string, number>;
};

export type PlayerScopedGetOptions = {
  /** Cached payloads older than this are refetched. Omit for session-long caching. */
  ttlMs?: number;
};

export function createPlayerScopedBucket<T>(): PlayerScopedBucket<T> {
  return { data: new Map(), inflight: new Map(), loadedAt: new Map() };
}

export function playerScopedKey(userId: string, casinoId?: string | number | null): string {
  const u = String(userId ?? '').trim();
  if (!u) return '';
  if (casinoId === undefined || casinoId === null || String(casinoId).trim() === '') {
    return u;
  }
  return `${u}::${String(casinoId).trim()}`;
}

export function isPlayerScopedLoaded<T>(bucket: PlayerScopedBucket<T>, key: string): boolean {
  return !!key && bucket.data.has(key);
}

export function getPlayerScopedCached<T>(bucket: PlayerScopedBucket<T>, key: string): T | undefined {
  return key ? bucket.data.get(key) : undefined;
}

export function invalidatePlayerScoped<T>(bucket: PlayerScopedBucket<T>, key?: string): void {
  if (key) {
    bucket.data.delete(key);
    bucket.inflight.delete(key);
    bucket.loadedAt.delete(key);
    return;
  }
  bucket.data.clear();
  bucket.inflight.clear();
  bucket.loadedAt.clear();
}

/**
 * Shared GET: returns the cached value when this key already loaded;
 * otherwise the response Observable (deduped while in flight).
 */
export function sharedPlayerScopedGet<T>(
  bucket: PlayerScopedBucket<T>,
  key: string,
  fetch: () => Observable<T>,
  options: PlayerScopedGetOptions = {}
): Observable<T> {
  if (!key) {
    return defer(() => fetch());
  }
  const cached = bucket.data.get(key);
  if (cached !== undefined) {
    const { ttlMs } = options;
    const loadedAt = bucket.loadedAt.get(key) ?? 0;
    const isStale = ttlMs !== undefined && Date.now() - loadedAt >= ttlMs;
    if (!isStale) return of(cached);
    bucket.data.delete(key);
    bucket.loadedAt.delete(key);
  }

  const existing = bucket.inflight.get(key);
  if (existing) return existing;

  // Only the request currently registered for this key may write the cache or
  // clear the in-flight slot. If it was invalidated mid-flight (and possibly
  // replaced by a newer request), it must not clobber the newer one.
  const isCurrent = () => bucket.inflight.get(key) === shared$;
  const shared$: Observable<T> = defer(() => fetch()).pipe(
    tap((resp) => {
      if (resp != null && isCurrent()) {
        bucket.data.set(key, resp);
        bucket.loadedAt.set(key, Date.now());
      }
    }),
    finalize(() => {
      if (isCurrent()) bucket.inflight.delete(key);
    }),
    shareReplay({ bufferSize: 1, refCount: false })
  );

  bucket.inflight.set(key, shared$);
  return shared$;
}

/** Named buckets used by player / auth hooks (typed loosely at the boundary). */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const favoritesListBucket = createPlayerScopedBucket<any>();
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const playlistListBucket = createPlayerScopedBucket<any>();
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const selectedEgmsListBucket = createPlayerScopedBucket<any>();
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const userMessagesListBucket = createPlayerScopedBucket<any>();
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const linkedAccountsBucket = createPlayerScopedBucket<any>();
export const challengesListBucket = createPlayerScopedBucket<IGetChallengesResponse>();

/**
 * GetChallenges can change server-side (new invites, joins), so its cache is
 * short-lived. Long enough to absorb Explore list remounts (first load,
 * timeframe/insight changes) without re-requesting.
 */
export const CHALLENGES_CACHE_TTL_MS = 60 * 1000;

/** Cache key for GetChallenges: user + kpCasinoPk + account type. */
export function challengesCacheKey(
  userId: string | null | undefined,
  kpCasinoPk: string | number,
  isCasinoAccount: boolean
): string {
  return playerScopedKey(
    String(userId || 'anon'),
    `${String(kpCasinoPk)}:${isCasinoAccount ? 'casino' : 'player'}`
  );
}

/** Bust linked-accounts cache after an explicit link/unlink (must refetch). */
export function bustLinkedAccountsCache(userId: string): void {
  invalidatePlayerScoped(linkedAccountsBucket, playerScopedKey(userId));
}

/** Bust favorites list cache after add/remove so remounts cannot re-Set stale DataSet. */
export function bustFavoritesCache(userId: string, casinoId: string | number): void {
  invalidatePlayerScoped(favoritesListBucket, playerScopedKey(userId, casinoId));
}

/** Bust playlist list cache after add/remove. */
export function bustPlaylistCache(userId: string, casinoId: string | number): void {
  invalidatePlayerScoped(playlistListBucket, playerScopedKey(userId, casinoId));
}

/**
 * Patch a cached list response's DataSet in place (keeps session cache aligned with
 * Redux optimistic add/remove without forcing a network refetch).
 */
export function patchPlayerScopedDataSet<T extends { DataSet?: unknown[] }>(
  bucket: PlayerScopedBucket<T>,
  key: string,
  nextDataSet: unknown[]
): void {
  if (!key) return;
  const prev = bucket.data.get(key);
  if (!prev || typeof prev !== 'object') return;
  bucket.data.set(key, { ...prev, DataSet: nextDataSet } as T);
}

/** Test helper — clears every player-scoped list bucket. */
export function resetAllPlayerScopedListCaches(): void {
  invalidatePlayerScoped(favoritesListBucket);
  invalidatePlayerScoped(playlistListBucket);
  invalidatePlayerScoped(selectedEgmsListBucket);
  invalidatePlayerScoped(userMessagesListBucket);
  invalidatePlayerScoped(linkedAccountsBucket);
  invalidatePlayerScoped(challengesListBucket);
}
